// ============================================================
// Integrations.js — drop into src/ alongside the existing screens.
// Two sections: (1) global Hostex/Tuya credentials, (2) per-property
// access method picker. Mirrors the Settings.js patterns already
// in the dashboard (card grid, upload-box-style panels, toast prop).
// ============================================================

import { useState, useEffect, useCallback } from 'react';

const IconTuya = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 018 0v3"/></svg>;
const IconLockbox = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="12" cy="12" r="4"/><path d="M12 10v2l1.5 1"/></svg>;
const IconManual = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2"/><circle cx="10" cy="7" r="4"/></svg>;
const IconCheck = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>;

const ACCESS_METHODS = [
  { key: 'tuya', label: 'Tuya', icon: IconTuya },
  { key: 'lockbox', label: 'Lockbox', icon: IconLockbox },
  { key: 'manual', label: 'Manuel', icon: IconManual },
];

function IntegrationField({ label, sub, fieldKey, currentValue, onSave, secret }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await onSave(fieldKey, value.trim());
    setSaving(false);
    setEditing(false);
    setValue('');
  };

  return (
    <div className="integration-field">
      <div>
        <div className="integration-field-label">{label}</div>
        {sub && <div className="integration-field-sub">{sub}</div>}
      </div>
      <div className="integration-field-value">
        {editing ? (
          <>
            <input
              className="fi"
              style={{ width: 180, padding: '6px 10px', fontSize: 12 }}
              type={secret ? 'password' : 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Nouvelle valeur"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <button className="btn btn-p btn-sm" onClick={handleSave} disabled={saving}>
              {saving ? <span className="spin" /> : IconCheck}
            </button>
            <button className="btn btn-s btn-sm" onClick={() => { setEditing(false); setValue(''); }}>
              Annuler
            </button>
          </>
        ) : (
          <>
            <span className={`integration-status-dot ${currentValue?.is_set ? 'on' : 'off'}`} />
            {currentValue?.is_set && (
              <span className="integration-masked-value">{currentValue.value}</span>
            )}
            <button className="btn btn-s btn-sm" onClick={() => setEditing(true)}>
              {currentValue?.is_set ? 'Modifier' : 'Configurer'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PropertyAccessCard({ property, m, toast }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    m(`/admin/properties/${property.id}/access`).then(r => r.json())
      .then(data => { if (data.success) setConfig(data.data); })
      .finally(() => setLoading(false));
  }, [property.id, m]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    const res = await m(`/admin/properties/${property.id}/access`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
    const data = await res.json();
    if (data.success) { setConfig(data.data); toast('Configuration mise à jour', 'ok'); }
    else toast(data.error || 'Erreur', 'err');
  };

  if (loading || !config) {
    return <div className="card" style={{ padding: 16 }}><span className="spin" /></div>;
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-hdr">
        <div className="card-title">{property.name}</div>
      </div>
      <div style={{ padding: 16 }}>
        <div className="access-method-picker">
          {ACCESS_METHODS.map(method => (
            <div
              key={method.key}
              className={`access-method-opt ${config.access_method === method.key ? 'on' : ''}`}
              onClick={() => patch({ access_method: method.key })}
            >
              <div className="access-method-opt-icon">{method.icon}</div>
              <div className="access-method-opt-label">{method.label}</div>
            </div>
          ))}
        </div>

        {config.access_method === 'tuya' && (
          <div className="ff">
            <label className="fl">Tuya device ID</label>
            <input
              className="fi"
              defaultValue={config.tuya_device_id || ''}
              onBlur={(e) => e.target.value !== config.tuya_device_id && patch({ tuya_device_id: e.target.value })}
              placeholder="tuya_dev_..."
            />
          </div>
        )}

        {config.access_method === 'lockbox' && (
          <div className="ff">
            <label className="fl">Code de la boîte à clé</label>
            <input
              className="fi"
              defaultValue={config.lockbox_code || ''}
              onBlur={(e) => e.target.value !== config.lockbox_code && patch({ lockbox_code: e.target.value })}
              placeholder="5521"
            />
          </div>
        )}

        {config.access_method === 'manual' && (
          <div className="ff">
            <label className="fl">Note pour l'admin</label>
            <textarea
              className="fta"
              style={{ minHeight: 70 }}
              defaultValue={config.manual_note || ''}
              onBlur={(e) => e.target.value !== config.manual_note && patch({ manual_note: e.target.value })}
              placeholder="Ex : l'admin remet la clé en main propre"
            />
          </div>
        )}

        <div className="ff">
          <label className="fl">Instructions d'entrée (interphone, portail...)</label>
          <input
            className="fi"
            defaultValue={config.entrance_instructions || ''}
            onBlur={(e) => e.target.value !== config.entrance_instructions && patch({ entrance_instructions: e.target.value })}
            placeholder="Interphone 3B — grille d'accès au portail: 8842"
          />
        </div>

        <div className="ff" style={{ marginBottom: 0 }}>
          <label className="fl">Vidéo d'accès (remplace la vidéo par défaut)</label>
          <input
            className="fi"
            defaultValue={config.access_video_url || ''}
            onBlur={(e) => e.target.value !== config.access_video_url && patch({ access_video_url: e.target.value })}
            placeholder="https://youtube.com/... (laisser vide pour utiliser la vidéo par défaut)"
          />
        </div>
      </div>
    </div>
  );
}

export default function Integrations({ properties, m, toast }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    m('/admin/integrations').then(r => r.json())
      .then(data => { if (data.success) setSettings(data.data); })
      .finally(() => setLoading(false));
  }, [m]);

  useEffect(() => { load(); }, [load]);

  const saveField = async (key, value) => {
    const res = await m(`/admin/integrations/${key}`, { method: 'PUT', body: JSON.stringify({ value }) });
    const data = await res.json();
    if (data.success) { toast('Enregistré', 'ok'); load(); }
    else toast(data.error || 'Erreur', 'err');
  };

  const generateWebhookSecret = async () => {
    const res = await m('/admin/integrations/hostex-webhook/generate-secret', { method: 'POST' });
    const data = await res.json();
    if (data.success) { toast('Secret généré', 'ok'); load(); }
    else toast(data.error || 'Erreur', 'err');
  };

  return (
    <div className="content fia">
      <div className="integrations-grid" style={{ marginBottom: 14 }}>
        <div className="card">
          <div className="card-hdr">
            <div className="card-title">Hostex</div>
          </div>
          <div style={{ padding: '4px 16px' }}>
            {loading ? <div style={{ padding: 16 }}><span className="spin" /></div> : (
              <>
                <IntegrationField
                  label="Clé API"
                  fieldKey="hostex_api_key"
                  currentValue={settings.hostex_api_key}
                  onSave={saveField}
                  secret
                />
                <div className="integration-field">
                  <div>
                    <div className="integration-field-label">Secret webhook</div>
                    <div className="integration-field-sub">Pour vérifier la signature des requêtes Hostex</div>
                  </div>
                  <div className="integration-field-value">
                    <span className={`integration-status-dot ${settings.hostex_webhook_secret?.is_set ? 'on' : 'off'}`} />
                    {settings.hostex_webhook_secret?.is_set && (
                      <span className="integration-masked-value">{settings.hostex_webhook_secret.value}</span>
                    )}
                    <button className="btn btn-s btn-sm" onClick={generateWebhookSecret}>Générer</button>
                  </div>
                </div>
                <div className="integration-field">
                  <div>
                    <div className="integration-field-label">URL du webhook</div>
                    <div className="integration-field-sub">À coller dans le tableau de bord Hostex</div>
                  </div>
                  <div className="integration-field-value">
                    <span className="integration-masked-value" style={{ fontSize: 11 }}>
                      https://api.hospeo.com/api/hostex/webhook
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-hdr">
            <div className="card-title">Tuya</div>
          </div>
          <div style={{ padding: '4px 16px' }}>
            {loading ? <div style={{ padding: 16 }}><span className="spin" /></div> : (
              <>
                <IntegrationField
                  label="Client ID"
                  fieldKey="tuya_client_id"
                  currentValue={settings.tuya_client_id}
                  onSave={saveField}
                />
                <IntegrationField
                  label="Client Secret"
                  fieldKey="tuya_client_secret"
                  currentValue={settings.tuya_client_secret}
                  onSave={saveField}
                  secret
                />
                <IntegrationField
                  label="URL de base API"
                  sub="Ex : https://openapi.tuyaeu.com"
                  fieldKey="tuya_api_base_url"
                  currentValue={settings.tuya_api_base_url}
                  onSave={saveField}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-hdr">
          <div className="card-title">Vidéo d'accès par défaut</div>
        </div>
        <div style={{ padding: '4px 16px' }}>
          {!loading && (
            <IntegrationField
              label="Lien YouTube"
              sub="Utilisé quand une propriété n'a pas sa propre vidéo"
              fieldKey="default_access_video_url"
              currentValue={settings.default_access_video_url}
              onSave={saveField}
            />
          )}
        </div>
      </div>

      <div className="card-hdr" style={{ background: 'none', border: 'none', padding: '4px 0 12px' }}>
        <div className="card-title">Accès par propriété</div>
      </div>
      {properties.map(property => (
        <PropertyAccessCard key={property.id} property={property} m={m} toast={toast} />
      ))}
    </div>
  );
}
